/* Style / Color selectors on the product page (snippets/product-get-options.liquid,
   options_type 2). Loaded from sections/main-product.liquid.

   The theme's ProductOptions controller (theme.js) still owns selection: a
   click on [data-js-option-value] resolves the variant, toggles .active,
   updates the label value, price, gallery, availability and URL. This file
   only adds what a radio group needs on top of those divs:

   - aria-checked and a roving tabindex that follow .active (MutationObserver
     on the group, so it also tracks changes the theme makes from the gallery
     or a deep link)
   - keyboard: Enter/Space select, arrows move to the next enabled value and
     select it, Home/End jump; aria-disabled values are skipped

   Listeners are delegated from document, so section re-renders never double
   them; observers are attached once per group. */
(function () {
  'use strict';

  var GROUP = '.product-page-info__options .product-options--type-page [role="radiogroup"]';
  var VALUE = '[data-js-option-value]';

  function valuesOf(group) {
    return Array.prototype.slice.call(group.querySelectorAll(VALUE));
  }
  function enabled(el) { return !el.classList.contains('disabled'); }

  function sync(group) {
    var list = valuesOf(group);
    var active = null, first = null;
    list.forEach(function (el) {
      if (el.classList.contains('active') && !active) active = el;
      if (enabled(el) && !first) first = el;
    });
    var tabStop = active || first;
    if (active && active !== group.__acLastActive) { group.__acLastActive = active; reveal(active); }
    list.forEach(function (el) {
      el.setAttribute('aria-checked', el.classList.contains('active') ? 'true' : 'false');
      el.setAttribute('tabindex', el === tabStop ? '0' : '-1');
      if (enabled(el)) el.removeAttribute('aria-disabled'); else el.setAttribute('aria-disabled', 'true');
    });
  }

  function observe(group) {
    if (group.__acSelectors) return;
    group.__acSelectors = true;
    sync(group);
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; sync(group); });
    }).observe(group, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
  }

  /* ---- one sliding row: arrows, edge state, keep the chosen value in view ---- */

  var ARROW_SVG = {
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>'
  };

  function container(group) { return group.closest('[data-section-container]'); }

  function updateEdges(group) {
    var box = container(group);
    if (!box) return;
    var list = valuesOf(group);
    if (!list.length) return;
    /* Judge by the first and last values, not scrollWidth: the row's own
       padding would otherwise count as overflow and show an arrow when
       every value is already in view. */
    var g = group.getBoundingClientRect();
    var first = list[0].getBoundingClientRect(), last = list[list.length - 1].getBoundingClientRect();
    var span = last.right - first.left;
    var scrollable = span > g.width - 8;
    box.classList.toggle('is-scrollable', scrollable);
    box.classList.toggle('at-start', first.left >= g.left - 1);
    box.classList.toggle('at-end', last.right <= g.right + 1);
    box.style.setProperty('--ac-sel-row-top', (group.offsetTop - 10) + 'px');
  }

  function reveal(el) {
    var group = el && el.closest('[role="radiogroup"]');
    if (!group) return;
    var pad = 12;
    var left = el.offsetLeft - pad, right = el.offsetLeft + el.offsetWidth + pad;
    if (left < group.scrollLeft) group.scrollLeft = left;
    else if (right > group.scrollLeft + group.clientWidth) group.scrollLeft = right - group.clientWidth;
  }

  function addArrows(group) {
    var box = container(group);
    if (!box || box.querySelector('.ac-sel-arrow')) return;
    var label = group.getAttribute('aria-label') || 'options';
    ['prev', 'next'].forEach(function (dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ac-sel-arrow ac-sel-arrow--' + dir;
      b.setAttribute('aria-label', (dir === 'prev' ? 'Previous ' : 'More ') + label.toLowerCase() + ' options');
      b.innerHTML = ARROW_SVG[dir];
      b.addEventListener('click', function () {
        var step = Math.max(group.clientWidth * 0.7, 80);
        group.scrollLeft += dir === 'prev' ? -step : step;
      });
      box.appendChild(b);
    });
    var pending = false;
    var schedule = function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; updateEdges(group); });
    };
    group.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if (window.ResizeObserver) new ResizeObserver(schedule).observe(group);
    schedule();
    var active = group.querySelector(VALUE + '.active');
    if (active) reveal(active);
  }

  function init(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll(GROUP), function (group) {
      observe(group);
      addArrows(group);
    });
  }

  document.addEventListener('keydown', function (event) {
    var el = event.target;
    if (!el || !el.matches || !el.matches(GROUP + ' ' + VALUE)) return;
    var group = el.closest('[role="radiogroup"]');
    var list = valuesOf(group).filter(enabled);
    var index = list.indexOf(el);
    var next = null;
    switch (event.key) {
      case ' ':
      case 'Enter':
        event.preventDefault();
        if (!el.classList.contains('active')) el.click();
        return;
      case 'ArrowRight':
      case 'ArrowDown':
        next = list[(index + 1) % list.length]; break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = list[(index - 1 + list.length) % list.length]; break;
      case 'Home':
        next = list[0]; break;
      case 'End':
        next = list[list.length - 1]; break;
      default:
        return;
    }
    event.preventDefault();
    if (!next || next === el) return;
    reveal(next);
    next.focus({ preventScroll: true });
    if (!next.classList.contains('active')) next.click();
  });

  /* The theme's tooltips (tippy) show a value's name on hover; show it on
     keyboard focus too, so the name is visible as well as announced. */
  function tip(el, show) {
    var t = el && el._tippy;
    if (!t) return;
    try { show ? t.show() : t.hide(); } catch (e) {}
  }
  document.addEventListener('focusin', function (event) {
    var el = event.target;
    if (el && el.matches && el.matches(GROUP + ' ' + VALUE)) { reveal(el); if (el.matches(':focus-visible')) tip(el, true); }
  });
  document.addEventListener('focusout', function (event) {
    var el = event.target;
    if (el && el.matches && el.matches(GROUP + ' ' + VALUE)) tip(el, false);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
